import { Theme } from "theme-ui";
import { themeColor } from "./theme-helper";

export * from "./base";

const greyColors = {
  6: `color-mix(in srgb, ${themeColor("variables._darkest")}, transparent 94%)`,
  10: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 90%)`,
  20: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 80%)`,
  30: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 70%)`,
  40: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 60%)`,
  50: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 50%)`,
  60: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 40%)`,
  70: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 30%)`,
  80: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 20%)`,
  90: `color-mix(in srgb, ${themeColor(
    "variables._darkest"
  )}, transparent 10%)`,
};

type KameleonThemeType = {
  boxes: any;
  icons: any;
  timeline: any;
  paragraph: any;
  table: any;
  buttonGroups: any;
} & Theme;

export const KameleonBase: KameleonThemeType = {
  colors: {
    variables: {
      _neutral: "#FCF7EF",
      _mainCTA: "#FCA311",
      _secondaryCTA: "#1F4DB1",
      _dark: "#14213D",
      _darkest: "#0D1526",
      _neutralLight: "#FFFFFF",
      // Allow both "grays" and "greys"
      greys: greyColors,
      grays: greyColors,
    },
    logoFilter: "none",
    modes: {
      dark: {
        elements: {
          hr: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 60%)`,
        },
        overlays: {
          linearGradient: `linear-gradient(90deg, ${themeColor(
            "variables.greys.90"
          )} 0%, ${themeColor("variables.greys.70")} 100%)`,
          opacity: themeColor("variables.greys.50"),
        },
        bg: {
          icon: `color-mix(in srgb, ${themeColor("primary")}, transparent 90%)`,
          cta: themeColor("variables._dark"),
          button: themeColor("variables._darkest"),
        },
        links: {
          navHover: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 20%)`,
          navActive: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 30%)`,
          hover: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 20%)`,
          active: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 30%)`,
        },
        buttons: {
          primaryText: themeColor("variables._darkest"),
          disabled: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 10%)`,
          backgroundHover: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 15%)`,
          backgroundActive: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 25%)`,
          secondaryBackgroundHover: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 90%)`,
          secondaryBackgroundActive: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 85%)`,
          accentBackgroundHover: `color-mix(in srgb, ${themeColor(
            "accent"
          )}, transparent 95%)`,
          accentBackgroundActive: `color-mix(in srgb, ${themeColor(
            "accent"
          )}, transparent 90%)`,
          tertiary: themeColor("variables._neutralLight"),
          tertiaryHover: themeColor("variables._mainCTA"),
          tertiaryIcon: themeColor("variables._neutralLight"),
          tertiaryIconHover: themeColor("variables._mainCTA"),
        },
        input: {
          slider: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 80%)`,
          border: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 40%)`,
          borderHover: `color-mix(in srgb, ${themeColor(
            "variables._neutralLight"
          )}, transparent 20%)`,
          borderFocus: themeColor("variables._neutralLight"),
        },
        boxes: {
          greyBg: `color-mix(in srgb, ${themeColor(
            "variables._dark"
          )} 50%, ${themeColor("variables._neutralLight")} 7%)`,
          greyBgFilled: `color-mix(in srgb, ${themeColor(
            "variables._dark"
          )} 50%, ${themeColor("variables._neutralLight")} 50%)`,
          accordion: `color-mix(in srgb, ${themeColor(
            "variables._dark"
          )}, ${themeColor("variables._neutralLight")} 10%)`,
          accordionItem: themeColor("variables._dark"),
        },
        cards: {
          primary: {
            bg: themeColor("variables._darkest"),
          },
        },
        navbar: {
          dropdown: themeColor("variables._darkest"),
        },
        logoFilter: "invert(100%)",
        text: themeColor("variables._neutralLight"),
        primary: themeColor("variables._neutralLight"),
        secondary: themeColor("variables._neutralLight"),
        mainCTA: themeColor("variables._mainCTA"),
        secondaryCTA: themeColor("variables._secondaryCTA"),
        dark: themeColor("variables._dark"),
        darkest: themeColor("variables._darkest"),
        background: themeColor("variables._darkest"),
        regular: themeColor("variables._darkest"),
        muted: themeColor("variables._dark"),
        greyTagline: themeColor("variables._neutralLight"),
        highlight: `color-mix(in srgb, ${themeColor(
          "variables._neutralLight"
        )}, transparent 90%)`,
        accent: themeColor("variables._secondaryCTA"),
        lightest: themeColor("variables._neutralLight"),
      },
    },
    /*
     * The default theme. Any other mode should include the exact same props under colors.modes.$mode as below
     */
    elements: {
      hr: themeColor("variables.greys.30"),
    },
    overlays: {
      linearGradient: `linear-gradient(90deg, ${themeColor(
        "variables.greys.90"
      )} 0%, ${themeColor("variables.greys.70")} 100%)`,
      opacity: themeColor("variables.greys.50"),
    },
    bg: {
      icon: `color-mix(in srgb, ${themeColor("primary")}, transparent 90%)`,
      cta: themeColor("variables._neutral"),
    },
    links: {
      navHover: `color-mix(in srgb, ${themeColor("secondary")}, black 40%)`,
      navActive: `color-mix(in srgb, ${themeColor("secondary")}, black 50%)`,
      hover: `color-mix(in srgb, ${themeColor("primary")}, black 40%)`,
      active: `color-mix(in srgb, ${themeColor("primary")}, black 50%)`,
    },
    buttons: {
      primaryText: themeColor("variables._neutralLight"),
      disabled: `color-mix(in srgb, ${themeColor("primary")}, black 10%)`,
      backgroundHover: `color-mix(in srgb, ${themeColor(
        "primary"
      )}, black 15%)`,
      backgroundActive: `color-mix(in srgb, ${themeColor(
        "primary"
      )}, black 25%)`,
      secondaryBackgroundHover: `color-mix(in srgb, ${themeColor(
        "secondary"
      )}, transparent 95%)`,
      secondaryBackgroundActive: `color-mix(in srgb, ${themeColor(
        "secondary"
      )}, transparent 90%)`,
      accentBackgroundHover: `color-mix(in srgb, ${themeColor(
        "accent"
      )}, transparent 95%)`,
      accentBackgroundActive: `color-mix(in srgb, ${themeColor(
        "accent"
      )}, transparent 90%)`,
      tertiary: themeColor("variables._darkest"),
      tertiaryHover: themeColor("variables._darkest"),
      tertiaryIcon: themeColor("variables._mainCTA"),
      tertiaryIconHover: `color-mix(in srgb, ${themeColor(
        "primary"
      )}, black 40%)`,
    },
    input: {
      slider: themeColor("variables.grays.20"),
      border: themeColor("variables.grays.30"),
      borderHover: themeColor("variables.grays.50"),
      borderFocus: themeColor("variables._darkest"),
    },
    boxes: {
      greyBg: themeColor("variables.greys.6"),
      greyBgFilled: `color-mix(in srgb, ${themeColor(
        "variables._darkest"
      )} 6%, ${themeColor("variables._neutralLight")} 94%)`,
      accordion: `color-mix(in srgb, ${themeColor("primary")}, ${themeColor(
        "variables._neutralLight"
      )} 80%)`,
      accordionItem: themeColor("variables._neutralLight"),
    },
    cards: {
      primary: {
        bg: themeColor("variables._neutralLight"),
      },
    },
    navbar: {
      dropdown: themeColor("variables._neutral"),
    },
    text: themeColor("variables._darkest"),
    primary: themeColor("variables._mainCTA"),
    secondary: themeColor("variables._secondaryCTA"),
    background: themeColor("variables._neutralLight"),
    regular: themeColor("variables._neutralLight"),
    muted: themeColor("variables._neutral"),
    greyTagline: themeColor("variables.greys.60"),
    highlight: `color-mix(in srgb, ${themeColor(
      "variables._mainCTA"
    )}, transparent 90%)`,
    accent: themeColor("variables._secondaryCTA"),
    lightest: themeColor("variables._neutralLight"),
  },
  radii: [4, 8, 16, 32, 32],
  breakpoints: ["320px", "568px", "768px", "992px"],
  fonts: {
    heading: "Jost, sans-serif",
    body: "Source Sans Pro, sans-serif",
  },
  fontSizes: [
    "0.75rem",
    "0.875rem",
    "1rem",
    "1.125rem",
    "1.25rem",
    "1.5rem",
    "1.75rem",
    "2rem",
    "2.25rem",
    "2.5rem",
    "2.75rem",
    "3rem",
    "3.25rem",
    "3.5rem",
    "5.5rem",
    "7.5rem",
    "10rem",
  ],
  fontWeights: {
    body: 400,
    heading: 700,
    bold: 700,
  },
  lineHeights: {
    body: 1.5,
    heading: 1.125,
  },
  text: {
    heading: {
      fontFamily: "heading",
      lineHeight: "heading",
      fontWeight: "heading",
    },
    giant: {
      fontSize: [11, 11, 14, 15, 16],
      fontWeight: "700",
      wordBreak: "break-word",
    },
    large: {
      fontSize: [3, null, 4],
      wordBreak: "break-word",
    },
    medium: {
      fontSize: [2, null, 3],
      wordBreak: "break-word",
    },
    default: {
      fontSize: [1, null, 2],
      wordBreak: "break-word",
    },
    small: {
      fontSize: 1,
      wordBreak: "break-word",
    },
    tiny: {
      fontSize: 0,
      wordBreak: "break-word",
    },
    tagline: {
      fontSize: 2,
      fontWeight: "bold",
      wordBreak: "break-word",
    },
  },
  styles: {
    root: {
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
      fontFamily: "body",
      lineHeight: "body",
      fontWeight: "body",
    },
    h1: {
      variant: "text.heading",
      fontSize: [9, null, 12, 13],
      lineHeight: 1.2,
    },
    h2: {
      variant: "text.heading",
      fontSize: [8, null, 10, 11],
      lineHeight: 1.2,
    },
    h3: {
      variant: "text.heading",
      fontSize: [7, null, 8, 9],
      lineHeight: 1.2,
    },
    h4: {
      variant: "text.heading",
      fontSize: [5, null, 6, 7],
      lineHeight: 1.3,
    },
    h5: {
      variant: "text.heading",
      fontSize: [4, null, 5],
      lineHeight: 1.4,
    },
    h6: {
      variant: "text.heading",
      fontSize: [3, null, 4],
      lineHeight: 1.4,
    },
    a: {
      textDecoration: "none",
      color: themeColor("primary"),
      ":hover": {
        color: themeColor("links.hover"),
      },
      ":visited": {
        color: themeColor("primary"),
      },
    },
    p: {
      fontSize: 2,
    },
    b: {
      fontSize: 2,
      fontWeight: "bold",
    },
    hr: {
      borderBottom: `1px solid ${themeColor("elements.hr")}`,
      borderRadius: 99999,
    },
    spinner: {
      color: themeColor("primary"),
    },
    progress: {
      color: themeColor("primary"),
      backgroundColor: themeColor("variables.greys.10"),
    },
  },
  paragraph: {
    primaryNumber: {
      color: "primary",
      fontSize: 5,
      fontWeight: "bold",
    },
  },
  table: {
    periodText: {
      display: "inline",
      fontSize: "1.5rem",
      "@media screen and (max-width: 767px)": {
        fontSize: "1.25rem",
      },
      "@media screen and (max-width: 479px)": {
        fontSize: ".875rem",
      },
    },
    priceText: {
      fontWeight: "700",
      fontSize: "3.5rem",
      wordBreak: "break-word",
      "@media screen and (max-width: 991px)": {
        fontSize: "3.25rem",
      },
      "@media screen and (max-width: 767px)": {
        fontSize: "2.5rem",
      },
      "@media screen and (max-width: 479px)": {
        fontSize: "1.5rem",
      },
    },
  },
  timeline: {
    progress: {
      backgroundColor: themeColor("elements.hr"),
    },
    indicator: {
      backgroundColor: themeColor("variables._mainCTA"),
    },
    circle: {
      boxShadow: "0 0 0 8px",
      color: "background",
      backgroundColor: themeColor("variables._mainCTA"),
    },
    background: {
      backgroundColor: "background",
    },
    fade: {
      top: {
        backgroundImage:
          "linear-gradient(to bottom, var(--theme-ui-colors-background), rgba(255, 255, 255, 0))",
      },
      bottom: {
        backgroundImage:
          "linear-gradient(to top, var(--theme-ui-colors-background), rgba(255, 255, 255, 0))",
      },
    },
  },
  shadows: {
    small: `0 0 4px ${themeColor("variables.greys.10")}`,
    large: `0 0 24px ${themeColor("variables.greys.10")}`,
    button: `0 4px 8px -4px ${themeColor("variables.greys.40")};`,
  },
  buttons: {
    primary: {
      color: "buttons.primaryText",
      bg: "primary",
      cursor: "pointer",
      borderRadius: "0",
      font: "body",
      padding: "0.75rem 1.5rem",
      transition: "all .1s ease-in-out",
      "&:focus": {
        bg: themeColor("buttons.backgroundActive"),
      },
      "&:hover": {
        bg: themeColor("buttons.backgroundHover"),
      },
      "&:active": {
        bg: themeColor("buttons.backgroundActive"),
      },
      "&:disabled": {
        bg: themeColor("buttons.disabled"),
        cursor: "not-allowed",
      },
    },
    secondary: {
      color: themeColor("secondary"),
      background: "none",
      cursor: "pointer",
      border: `1px solid ${themeColor("secondary")}`,
      borderRadius: 0,
      padding: "0.75rem 1.5rem",
      transition: "all .1s ease-in-out",
      "&:focus": {
        bg: themeColor("buttons.secondaryBackgroundHover"),
      },
      "&:hover": {
        bg: themeColor("buttons.secondaryBackgroundHover"),
      },
      "&:active": {
        bg: themeColor("buttons.secondaryBackgroundActive"),
      },
    },
    slider: {
      default: {
        color: `${themeColor("secondary")} !important`,
        background: "none !important",
        cursor: "pointer",
        border: `1px solid ${themeColor("secondary")} !important`,
        borderRadius: 0,
        transition: "all .1s ease-in-out",
        "&:focus": {
          bg: `${themeColor("buttons.secondaryBackgroundHover")} !important`,
        },
        "&:hover": {
          bg: `${themeColor("buttons.secondaryBackgroundHover")} !important`,
        },
        "&:active": {
          bg: `${themeColor("buttons.secondaryBackgroundActive")} !important`,
        },
      },
      onMediaBackground: {
        // Use this variant when the buttons are on media in a slider. Because there's no overlay between the media and the button, a full, collared background with border will make sure it's always visible.
        color: themeColor("lightest"),
        bg: themeColor("accent"),
        cursor: "pointer",
        border: `1px solid ${themeColor("lightest")}`,
        borderRadius: 0,
        transition: "all .1s ease-in-out",
        "&:focus": {
          bg: themeColor("buttons.accentBackgroundHover"),
        },
        "&:hover": {
          bg: themeColor("buttons.accentBackgroundHover"),
        },
        "&:active": {
          bg: themeColor("buttons.accentBackgroundActive"),
        },
      },
    },
    tertiary: {
      text: {
        display: "flex",
        alignItems: "center",
        textDecoration: "none",
        padding: "0",
        marginLeft: "1rem",
        color: themeColor("buttons.tertiary"),
        background: "none",
        cursor: "pointer",
        "&:hover": {
          color: themeColor("buttons.tertiaryHover"),
          "& svg": {
            color: themeColor("buttons.tertiaryIconHover"),
          },
        },
      },
      icon: {
        color: themeColor("buttons.tertiaryIcon"),
        cursor: "pointer",
        transition: "all .1s ease-in-out",
        "&:focus": {
          color: themeColor("buttons.tertiaryIconHover"),
        },
        "&:hover": {
          color: themeColor("buttons.tertiaryIconHover"),
        },
        "&:active": {
          color: themeColor("buttons.tertiaryIconHover"),
        },
      },
    },
    menu: {
      color: themeColor("text"),
      "&:hover": {
        opacity: "60%",
      },
      "&:active": {
        opacity: "40%",
      },
    },
    close: {
      color: themeColor("text"),
      "&:hover": {
        opacity: "60%",
      },
      "&:active": {
        opacity: "40%",
      },
    },
  },
  buttonGroups: {
    gridColumnGap: "1rem",
    gridRowGap: "1rem",
    flexWrap: "wrap",
    alignItems: "center",
    display: "inline-flex",
  },
  links: {
    primary: {
      color: themeColor("primary"),
      background: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("links.hover"),
      },
      "&:hover": {
        color: themeColor("links.hover"),
      },
      "&:active": {
        color: themeColor("links.active"),
      },
    },
    underline: {
      textDecoration: "underline",
      color: themeColor("secondary"),
      background: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("links.navHover"),
      },
      "&:hover": {
        color: themeColor("links.navHover"),
      },
      "&:active": {
        color: themeColor("links.navActive"),
      },
    },
    text: {
      color: themeColor("text"),
      fontWeight: "bold",
      textDecoration: "none",
      background: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("text"),
      },
      "&:hover": {
        color: themeColor("text"),
      },
      "&:active": {
        color: themeColor("text"),
      },
    },
    nav: {
      color: themeColor("text"),
      background: "none",
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      fontWeight: "normal",
      "&:focus": {
        color: themeColor("links.navHover"),
      },
      "&:hover": {
        color: themeColor("links.navHover"),
      },
      "&.active": {
        borderBottom: `2px solid ${themeColor("secondary")}`,
        color: themeColor("secondary"),
      },
    },
    tab: {
      color: themeColor("text"),
      background: "none",
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      fontWeight: "normal",
      borderBottom: `1px solid ${themeColor("elements.hr")}`,
      opacity: "60%",
      "&.w--current": {
        borderBottom: `2px solid ${themeColor("primary")}`,
        backgroundColor: `color-mix(in srgb, ${themeColor(
          "primary"
        )}, transparent 85%)`,
        opacity: "100%",
      },
    },
    tabActiveLeft: {
      "&.w--current": {
        borderLeftColor: themeColor("primary"),
      },
      "&.w--current > * .tabs-link__content": {
        maxHeight: "1000px",
        transition: "max-height 0.60s ease-in",
      },
    },
    navMenu: {
      color: themeColor("text"),
      background: "none",
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      fontWeight: "normal",
      padding: "1rem 1.5rem",
      border: `1px ${themeColor("boxes.greyBgFilled")}`,
      borderBottom: `2px solid ${themeColor("boxes.greyBgFilled")}`,
      "&:focus": {
        color: themeColor("links.navHover"),
      },
      "&:hover": {
        color: themeColor("links.navHover"),
      },
      "&.w--current": {
        color: themeColor("secondary"),
        borderColor: themeColor("secondary"),
      },
    },
    heading: {
      color: themeColor("text"),
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("links.navHover"),
      },
      "&:hover": {
        color: themeColor("links.navHover"),
      },
      "&:active": {
        color: themeColor("links.navActive"),
      },
    },
    hoverHeader: {
      color: themeColor("text"),
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("links.navHover"),
      },
      "&:active": {
        color: themeColor("links.navActive"),
      },
    },
    tabHeading: {
      color: themeColor("text"),
      textDecoration: "none",
      cursor: "pointer",
      transition: "all .1s ease-in-out",
      "&:focus": {
        color: themeColor("primary"),
      },
      "&:hover": {
        color: themeColor("buttons.backgroundHover"),
      },
      "&:active": {
        color: themeColor("primary"),
      },
    },
  },
  icons: {
    sizes: {
      tiny: {
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        color: "primary",
        fontSize: "1rem",
        width: "2rem",
        height: "2rem",
      },
      small: {
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        color: "primary",
        fontSize: "1.5rem",
      },
      medium: {
        borderRadius: "50%",
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        color: "primary",
        fontSize: "2rem",
      },
    },
    rating: {
      color: themeColor("variables._mainCTA"),
      fontSize: "2rem",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
    },
    tertiary: {
      color: "primary",
      borderRadius: "50%",
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      fontSize: "1.5rem",
      width: "2rem",
      height: "3rem",
    },
  },
  images: {
    avatar: {
      width: "3.5rem",
      height: "3.5rem",
      borderRadius: 99999,
      objectFit: "cover",
    },
    // Reserved for the image variant that is no full-sized background. So basically the default image.
    nonBackground: {
      borderRadius: 0,
    },
    circle: {
      clipPath: "circle()",
    },
    card: {
      horizontal: {
        borderTopLeftRadius: "4px",
        borderTopRightRadius: "4px",
      },
      vertical: {
        borderTopLeftRadius: "4px",
        borderBottomLeftRadius: "4px",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        position: "absolute",
        top: "0%",
        bottom: "0%",
        left: "0%",
        right: "0%",
      },
    },
  },
  grids: {
    odd: {
      backgroundColor: "regular",
    },
    even: {
      backgroundColor: "muted",
    },
    colors: {
      0: {
        backgroundColor: "mainCTA",
      },
      1: {
        backgroundColor: "secondaryCTA",
      },
      2: {
        backgroundColor: "dark",
      },
      3: {
        backgroundColor: "darkest",
      },
    },
  },
  transitions: {
    glow: {
      "0%": {
        boxShadow: "0 0 0 0 rgba(255, 203, 5, 0.8)",
      },
      "100%": {
        boxShadow: "0 0 0 4px rgba(255, 203, 5, 0)",
      },
    },
    jump: {
      "0%": {
        transform: "translateY(0)",
      },
      "50%": {
        transform: "translateY(-10px)",
      },
      "100%": {
        transform: "translateY(0)",
      },
    },
  },
  forms: {
    alerts: {
      error: {
        box: {
          backgroundColor: "#FFDEDE",
          padding: "0.875rem 1rem",
          marginTop: "1.5rem",
          borderRadius: "10px",
        },
        message: {
          color: "#E23939",
          fontWeight: "bold",
        },
      },
      success: {
        box: {
          backgroundColor: "#C6E7C6FF",
          padding: "0.875rem 1rem",
          marginTop: "1.5rem",
          borderRadius: "10px",
        },
        message: {
          // color: themeColor("text"),
          color: "black",
          fontWeight: "bold",
        },
      },
    },
    label: {
      variant: "styles.b",
      marginBottom: "0.5rem",
    },
    input: {
      height: "auto",
      minHeight: "2.75rem",
      marginBottom: 0,
      padding: ".5rem .75rem",
      borderRadius: 0,
      variant: "styles.p",
      borderColor: themeColor("input.border"),
      "&:hover": {
        borderColor: themeColor("input.borderHover"),
      },
      "&:focus,&:focus-visible": {
        outline: "none",
        borderColor: themeColor("input.borderFocus"),
      },
    },
    textarea: {
      height: "auto",
      marginBottom: 0,
      padding: ".5rem .75rem",
      borderRadius: 0,
      minHeight: "11.25rem",
      paddingTop: ".75rem",
      paddingBottom: ".75rem",
      overflow: "auto",
      width: "100%",
      resize: "vertical",
      maxHeight: "30rem",
      variant: "styles.p",
      borderColor: themeColor("input.border"),
      "&:hover": {
        borderColor: themeColor("input.borderHover"),
      },
      "&:focus,&:focus-visible": {
        outline: "none",
        borderColor: themeColor("input.borderFocus"),
      },
    },
    select: {
      variant: "styles.p",
      borderColor: themeColor("input.border"),
      bg: "transparent",
      "&:hover": {
        borderColor: themeColor("input.borderHover"),
      },
      "&:focus,&:focus-visible": {
        outline: "none",
        borderColor: themeColor("input.borderFocus"),
      },
    },
    radio: {
      accentColor: themeColor("primary"),
      color: themeColor("input.border"),
      "&:hover": {
        color: themeColor("input.borderHover"),
      },
    },
    checkbox: {
      accentColor: themeColor("primary"),
      color: themeColor("input.border"),
      "&:hover": {
        color: themeColor("input.borderHover"),
      },
    },
    slider: {
      bg: themeColor("input.slider"),
      color: themeColor("primary"),
      "&:hover": {
        color: themeColor("buttons.backgroundHover"),
      },
      "&:active": {
        color: themeColor("buttons.backgroundActive"),
      },
    },
  },
  cards: {
    primary: {
      bg: themeColor("cards.primary.bg"),
      boxShadow: `0 8px 32px ${themeColor("variables.grays.10")}`,
      borderRadius: "0",
      overflowWrap: "break-word",
    },
    padding: {
      bg: themeColor("cards.primary.bg"),
      boxShadow: `0 8px 32px ${themeColor("variables.grays.10")}`,
      borderRadius: "0",
      overflowWrap: "break-word",
      padding: "2rem",
      "@media screen and (max-width: 767px)": {
        padding: "1.5rem",
      },
    },
    vertical: {
      default: {
        display: "grid",
        gridColumnGap: "2rem",
        gridRowGap: "1rem",
        gridTemplateRows: "auto",
        gridTemplateColumns: "0.75fr 1fr",
        gridAutoColumns: "1fr",
        alignItems: "stretch",
        "@media screen and (max-width: 767px)": {
          gridRowGap: "1.5rem",
          gridTemplateColumns: "1fr",
        },
        "@media screen and (max-width: 479px)": {
          gridRowGap: "1.5rem",
          gridTemplateColumns: "1fr",
          gridTemplateRows: "auto auto",
        },
      },
      content: {
        paddingTop: "2rem",
        paddingBottom: "2rem",
        paddingRight: "2rem",
        "@media screen and (max-width: 767px)": {
          paddingTop: "0",
          paddingBottom: "1.5rem",
          paddingRight: "1.5rem",
          paddingLeft: "1.5rem",
        },
      },
      wrapper: {
        width: "100%",
        height: "100%",
        paddingTop: "100%",
        position: "relative",
        overflow: "hidden",
        "@media screen and (max-width: 767px)": {
          height: "100%",
        },
        "@media screen and (max-width: 479px)": {
          paddingTop: "66%",
        },
      },
    },
    horizontal: {
      default: {},
      content: {
        paddingLeft: "2rem",
        paddingBottom: "2rem",
        paddingRight: "2rem",
        "@media screen and (max-width: 767px)": {
          paddingBottom: "1.5rem",
          paddingRight: "1.5rem",
          paddingLeft: "1.5rem",
        },
      },
    },
  },
  boxes: {
    backgrounds: {
      grey: {
        backgroundColor: themeColor("highlight"),
      },
      greyFilled: {
        backgroundColor: themeColor("boxes.greyBgFilled"),
      },
      primaryHighlight: {
        borderLeft: `solid 2px ${themeColor("primary")}`,
        backgroundColor: themeColor("highlight"),
      },
      accordion: {
        backgroundColor: themeColor("boxes.accordion"),
        item: {
          backgroundColor: themeColor("boxes.accordionItem"),
        },
      },
    },
    logo: {
      filter: themeColor("logoFilter"),
    },
    header: {
      navbar: {
        boxShadow: "small",
        width: "100%",
        dropdown: {
          boxShadow: "small",
          borderRadius: "1",
          backgroundColor: themeColor("navbar.dropdown"),
        },
        menuIcon: {
          width: "48px",
          alignItems: "center",
          transform: "rotate(0deg)",
          transition: ".5s ease-in-out",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          span: {
            height: "2px",
            width: "24px",
            alignItems: "center",
            background: themeColor("primary"),
            borderRadius: "1",
            opacity: 1,
            transform: "rotate(0deg)",
            transition: ".25s ease-in-out",
            "&:nth-of-type(2)": { marginBottom: "6px", marginTop: "6px" },
          },
        },
        menuIconOpen: (theme: any) => ({
          ...theme.boxes.header.navbar.menuIcon,
          height: "48px",
          alignItems: "center",
          span: {
            ...theme.boxes.header.navbar.menuIcon.span,
            top: "20px",
            position: "absolute",
            "&:nth-of-type(1)": { transform: "rotate(135deg)" },
            "&:nth-of-type(2)": { opacity: 0 },
            "&:nth-of-type(3)": { transform: "rotate(-135deg)" },
          },
        }),
      },
    },
    footer: {
      borderRadius: "2",
    },
    lightbox: {
      ".zilch__lightbox-backdrop": {
        background: themeColor("variables.greys.80"),
      },
      ".zilch__lightbox-caption": {
        backgroundColor: themeColor("overlays.opacity"),
        fontSize: 2,
        fontWeight: "normal",
        color: themeColor("text"),
      },
    },
    slider: {
      boxShadow: "large",
      borderRadius: "0",
      slide: {
        backgroundColor: themeColor("muted"),
      },
      nav: {
        height: "1.75rem",
        fontSize: "0.5rem",
        "> .w-slider-dot": {
          backgroundColor: themeColor("boxes.greyBg"),
          borderRadius: "0",
          boxShadow: "small",
        },
        "> .w-slider-dot.w-active": {
          backgroundColor: themeColor("secondary"),
        },
      },
    },
    dividers: {
      vertical: {
        backgroundColor: themeColor("elements.hr"),
        width: "1px",
        alignSelf: "stretch",
      },
      horizontal: {
        alignSelf: "stretch",
        color: themeColor("elements.hr"),
      },
      circle: {
        color: "primary",
        marginLeft: "0.5rem",
        marginRight: "0.5rem",
      },
    },
    accordion: {
      horizontal: {
        color: themeColor("text"),
        borderColor: `${themeColor("elements.hr")} !important`,
      },
    },
    overlays: {
      opacity: {
        wrapper: {
          position: "absolute",
          inset: "0%",
          zIndex: "-1",
        },
        layer: {
          position: "absolute",
          inset: "0%",
          zIndex: "1",
          backgroundColor: themeColor("variables.greys.70"),
        },
      },
    },
    hoverContainer: {
      "@media screen and (min-width: 992px)": {
        "&:hover a": {
          color: `${themeColor("elements.hr")} !important`,
        },
        "&:hover a:hover": {
          color: `${themeColor("text")} !important`,
        },
      },
    },
  },
};

export type KameleonTheme = typeof KameleonBase;
